import { SkuCardProps } from '@/types/props/components/skus';
import imageLoader from '@/utilites/imageLoader';
import LoadingComponent from '@/components/common/LoadingComponent';
import SkuCardDescription from '../SkuCardDescription';
import translationFallback from '@/utilites/translations/translationFallback';
import { toDe } from '@/utilites/sku-operations';
import SkuCardTrendingPercent from '../SkuCardTrendingPercent';
import { faHeart as solidFaHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import LoadingImage from '@/components/common/LoadingImage';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'next-i18next';
import useWishlist from '@/hooks/useWishlist';
import { toFixedNumber } from '@/utilites/calculations';
import { SkuDTO } from '@/types/api/sku';
import useTrendingPercent from '@/hooks/useTrendingPercent';
import { SkuMainImage } from '../../../../../utilites/get-sku-main-image';
import { useAnalytics } from '@/hooks/useAnalytics';
import useEvents from '@/hooks/useEvents';
export default function SkuCardMobile({ sku }: SkuCardProps) {
  const trendingPercent = useTrendingPercent(sku?.orders || [], sku?.retailPrice || 0);
  const { isInWishlist, toggleFromWishlist } = useWishlist();
  const { t: tCommon } = useTranslation('common');
  const { getPageType } = useAnalytics();
  const { fireGlobalToBottleButtonClickEvent } = useEvents('', []);

  const handleSkuCardClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    if (sku) {
      fireGlobalToBottleButtonClickEvent({
        slug: sku.slug,
        product_name: sku.name || '',
        product_category: sku?.category?.name || '',
        product_price: sku.lowestAsk || 0,
        page_type: getPageType(),
        price_ask: sku.lowestAsk || 0,
        brand_name_page: sku?.brand?.name || '',
      });
      window.location.href = `/${sku.slug}`;
    }
  };

  return (
    <div className="tw-relative !tw-h-[282px]">
      <LoadingComponent
        as={'button'}
        loading={sku === null}
        componentProps={{
          className:
            'tw-bg-transparent tw-border-none tw-appearance-none position-absolute top-0 end-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-max-w-20 tw-w-[23%] tw-h-auto tw-aspect-square',
          'aria-label':
            sku === null
              ? 'loading'
              : isInWishlist(sku?.slug ?? '')
                ? 'Remove from wishlist'
                : 'Add to wishlist',
          onClick: e => {
            e.stopPropagation();
            toggleFromWishlist(sku as SkuDTO);
          },
        }}
      >
        <FontAwesomeIcon
          icon={isInWishlist(sku?.slug ?? '') ? solidFaHeart : faHeart}
          className={`tw-text-base ${
            isInWishlist(sku?.slug ?? '') ? 'text-danger' : 'text-muted'
          } `}
        />
      </LoadingComponent>
      <Card
        tabIndex={sku ? 0 : -1}
        as="a"
        href={`/${sku?.slug}`}
        onClick={handleSkuCardClick}
        aria-label={sku ? `Go to bottle ${sku.name}` : 'Link disabled'}
        className={clsx(
          { 'tw-pointer-events-none tw-overflow-hidden': !sku },
          'tw-solid tw-relative tw-flex !tw-h-full !tw-min-h-[282px] tw-w-full tw-flex-col tw-gap-[0.5rem] tw-rounded-[10px] tw-border tw-border-custom-gray tw-p-[.8rem] tw-text-xs-custom tw-text-black tw-no-underline'
        )}
      >
        <LoadingImage loading={sku === null} className="tw-h-[130px] tw-w-full">
          <div className="tw-h-[130px] tw-w-full tw-object-contain">
            <SkuMainImage
              width={144}
              height={130}
              alt={sku?.name ?? ''}
              loader={imageLoader}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              src={sku ? (sku.mainImageUrl as string) : ''}
            />
          </div>
        </LoadingImage>
        <div className="tw-mt-3 tw-min-h-[50.5px]">
          <LoadingComponent as="span" loading={sku === null}>
            <strong className="tw-mb-1 tw-line-clamp-2 tw-pb-[2px] tw-font-plusJakartaSans tw-text-[14px] tw-font-semibold tw-leading-[16.38px] tw-text-[#1A202C]">
              {sku === null ? 'Loading an awesome product...' : sku.name}
            </strong>
          </LoadingComponent>
          <LoadingComponent
            as="div"
            loading={sku === null}
            componentProps={{
              className:
                'tw-text-plusJakartaSans tw-font-normal tw-text-[12px] tw-text-slate-gray tw-leading-[13.86px]',
            }}
          >
            <SkuCardDescription sku={sku} />
          </LoadingComponent>
        </div>
        <div className="tw-mt-2 tw-w-full">
          <p className="tw-mb-0 tw-w-2/4 tw-font-plusJakartaSans tw-text-[12px] tw-font-medium tw-leading-[13.86px] tw-text-slate-gray">
            {translationFallback('lowest_ask', tCommon('lowest_ask'), 'Lowest ask')}
          </p>
          <div className="tw-flex tw-flex-wrap tw-flex-nowrap tw-items-start tw-justify-between">
            {sku === null ? (
              <LoadingComponent as="div" loading={true}>
                <span className="tw-font-semibold tw-leading-[18.77px] tw-text-textBlack">--</span>
              </LoadingComponent>
            ) : Number(sku.lowestAsk) > 0 ? (
              <div>
                <strong className="tw-text-fit tw-whitespace-nowrap tw-text-base tw-font-semibold tw-text-black">
                  {toDe(sku.lowestAsk ?? 0)}
                </strong>
              </div>
            ) : (
              <div>
                <span className="tw-font-semibold tw-leading-[18.77px] tw-text-textBlack">--</span>
              </div>
            )}
            <div className="tw-flex tw-shrink-0 tw-flex-grow tw-items-center tw-justify-end !tw-text-xs-lower !tw-font-medium">
              <SkuCardTrendingPercent
                sku={sku}
                trendingPercent={trendingPercent ? toFixedNumber(trendingPercent, 0) : 0}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
